import React, { useState, useEffect } from "react";
import { Input, DatePicker } from "antd";
import { useDispatch } from "react-redux";
import { SelectForm } from "../../../../common/Buttons";
import moment from "moment";
import { postComments } from "../../../../../redux/Actions/commentAction";
import { getNordrentOrder } from "../../../../../redux/Actions/orderAction";
import { FlopppyButton } from "../../../ButtonWithLoading";
import { getDateFormat } from "../../../../../helpers/Utils";
import { goods_not_arrived_reasons } from "../../../../../helpers/GlobalOptions";
import { useSelector } from "react-redux";

const { TextArea } = Input;

const GOODS_NOT_ARRIVED_STATE = "Goods did not arrive";
const NOTE_REMINDER_DEFAULT_TIME = "15:00";

const SubmitComment = (props) => {

  const dispatch = useDispatch();
  const validate_comments = useSelector(
    (state) => state.orders.validate_comments
  );
  const isMountedRef = React.useRef(true);
  const [InputValue, setInput] = useState("");
  const [Select, setSelect] = useState(undefined);
  const [DateInput, setDateInput] = useState(undefined);
  const [ErrorText, setErrorText] = useState(false);
  const [Options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [SkuSelect, setSkuSelect] = useState(undefined);
  const [ReasonSelect, setReasonSelect] = useState(undefined);
  let dateShow = props.options.find((item) => item.value == Select)
    ? props.options.find((item) => item.value == Select).show_date
    : false;

  const selectedOption = Options.find((item) => item.value == Select);
  const isNoteManagementState =
    props.type === "comment_management" &&
    selectedOption?.name?.toLowerCase() === "note";
  const isGoodsNotArrived =
    props.type === "comment_management" &&
    selectedOption?.name === GOODS_NOT_ARRIVED_STATE;

  // Products of the order, offered as "SKU — name" when tagging a missing item.
  const skuOptions = React.useMemo(
    () =>
      (props.order_rows || [])
        .filter(
          (row) =>
            row.product_reference &&
            row.product_reference !== "{}" &&
            row.product_name
        )
        .map((row) => ({
          value: row.id,
          sku: row.product_reference,
          label: `${row.product_reference} — ${row.product_name}`,
        })),
    [props.order_rows]
  );
  
  useEffect(() => {
    let options = [];
    props.options.forEach((e) => {
      if (e.comment_type == props.type) {
        if (e.name == "Call the customer") {
          options.splice(1, 0, e);
        } else {
          options.push(e);
        }
      }
    });
    setOptions(options);
  }, [props.options, props.type]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleDateInputChange = (date, formattedDate) => {
    if (!date) return setDateInput(undefined);
    return setDateInput(formattedDate);
  };


  const onSubmit = (e) => {
    e.preventDefault()
    if (isMountedRef.current) {
      setLoading(true);
      setErrorText(false);
    }
    const requiresReminderDateTime = isNoteManagementState;
    let dateTime = dateShow || requiresReminderDateTime ? DateInput : false;
    if ((dateShow || requiresReminderDateTime) && DateInput === undefined) {
      if (isMountedRef.current) setLoading(false);
      return setErrorText(
        requiresReminderDateTime
          ? "Enter reminder date and time"
          : "Enter the date"
      );
    }
    if (Select == undefined) {
      if (isMountedRef.current) setLoading(false);
      return setErrorText("Select the type of the comment");
    }

    let body = InputValue;
    if (isGoodsNotArrived) {
      if (SkuSelect == undefined) {
        if (isMountedRef.current) setLoading(false);
        return setErrorText("Select the product (SKU)");
      }
      if (ReasonSelect == undefined) {
        if (isMountedRef.current) setLoading(false);
        return setErrorText("Select the reason");
      }
      const sku = skuOptions.find((item) => item.value == SkuSelect)?.sku;
      const autoLine = `${sku} - ${moment().format("YYYY-MM-DD")} - ${ReasonSelect}`;
      // Auto-composed SKU + reason line; any typed note is appended below it.
      body = InputValue ? `${autoLine}\n${InputValue}` : autoLine;
    }

    let variablesBody = {
      order_id: props.orderId,
      comment: {
        comment_state_id: Select,
        body: body,
        comment_type: props.type,
      },
    };
    if (dateTime) {
      const dateFormat = requiresReminderDateTime
        ? "YYYY-MM-DD HH:mm:ss"
        : "YYYY-MM-DD";
      variablesBody = {
        order_id: props.orderId,
        comment: {
          comment_state_id: Select,
          body: body,
          comment_type: props.type,
          date: moment(DateInput).format(dateFormat),
        },
      };
    }

    return dispatch(postComments(variablesBody)).then((res) => {
      if (res.success) {
        const refreshOrder = props.nordrent
          ? dispatch(getNordrentOrder(props.orderId))
          : Promise.resolve();

        return Promise.resolve(refreshOrder).then(() => {
          if (!isMountedRef.current) return;
          setInput("");
          setSelect(undefined);
          setDateInput(undefined);
          setSkuSelect(undefined);
          setReasonSelect(undefined);
          setErrorText(false);
          setLoading(false);
          dispatch({
            type: "VALIDATE_COMMENTS",
            payload: {
              [props.type]: false,
            },
          });
        });
      } else {
        if (!isMountedRef.current) return;
        setErrorText(res.error);
        setLoading(false);
      }
    });
  };

  return (
    <>
      <div>
        <div className="mt-2">
          <SelectForm
            onlyPlaceholder="Comment type"
            options={Options}
            value={Select}
            onChange={(e) => setSelect(e)}
          />
        </div>
      </div>
      <div className="my-2">
        {Select !== undefined && (dateShow || isNoteManagementState) && (
          <DatePicker
            placeholder={isNoteManagementState ? "Reminder date and time" : "Data"}
            style={{ width: "100%" }}
            showTime={
              isNoteManagementState
                ? { defaultValue: moment(NOTE_REMINDER_DEFAULT_TIME, "HH:mm") }
                : false
            }
            format={isNoteManagementState ? "YYYY-MM-DD HH:mm" : "YYYY-MM-DD"}
            disabledDate={(d) =>
              !d || d.isBefore(moment().format("YYYY-MM-DD"))
            }
            value={getDateFormat(DateInput)}
            onChange={handleDateInputChange}
          />
        )}
      </div>
      {isGoodsNotArrived && (
        <div className="mb-2">
          <div className="mb-2">
            <SelectForm
              onlyPlaceholder="Product (SKU)"
              options={skuOptions}
              value={SkuSelect}
              onChange={(e) => setSkuSelect(e)}
            />
          </div>
          <div>
            <SelectForm
              onlyPlaceholder="Reason"
              options={goods_not_arrived_reasons}
              value={ReasonSelect}
              onChange={(e) => setReasonSelect(e)}
            />
          </div>
        </div>
      )}
      <TextArea
        className="mb-2"
        placeholder={isGoodsNotArrived ? "Additional note (optional)" : "Comment"}
        value={InputValue}
        onChange={(e) => {
          const value = e.target.value;
          setInput(value);
          if (value) {
            if (!validate_comments[props.type]) {
              dispatch({
                type: "VALIDATE_COMMENTS",
                payload: {
                  [props.type]: true,
                },
              });
            }
          } else {
            if (validate_comments[props.type]) {
              dispatch({
                type: "VALIDATE_COMMENTS",
                payload: {
                  [props.type]: false,
                },
              });
            }
          }
        }}
      />
      <div className="d-flex justify-content-between">
        <div className="text-danger">{ErrorText}</div>

        <FlopppyButton loading={loading} onClick={(e) => onSubmit(e)} />
      </div>
    </>
  );
};

export default SubmitComment;
